import { ContactMonitoringEventRule } from "models/contact-monitoring-event-rule";
import { NOTIFICATION_STATUS_CD } from "models/monitoring-event-rule";
// import {
//   ContactMonitoringEventRulesStoreValidator,
// } from "validators/contact-monitoring-event-rules";

import {
  axiosInstance as Repository,
  RESULT_CD,
} from "../repository";
import {
  MonitoringEventRulesRepositoryError,
} from "./monitoring-event-rules";

const RESOURCE = "/monitoring-targets";

/* カスタム例外 */
class ContactMonitoringEventRulesRepositoryError extends MonitoringEventRulesRepositoryError {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.errorCd = ContactMonitoringEventRulesRepositoryError.getErrorCd();
  }

  static getErrorCd() {
    return "10010";
  }
}

class ContactMonitoringEventRuleAlreadyExistsError
  extends ContactMonitoringEventRulesRepositoryError {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.errorCd = ContactMonitoringEventRuleAlreadyExistsError.getErrorCd();
  }

  static getErrorCd() {
    return "10011";
  }
}

class ContactMonitoringEventRuleNotFoundError
  extends ContactMonitoringEventRulesRepositoryError {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.errorCd = ContactMonitoringEventRuleNotFoundError.getErrorCd();
  }

  static getErrorCd() {
    return "10012";
  }
}

/* API Response と modelの変換 */
function getInstance(item) {
  const monitoringTargetId = item.monitoring_target_id;
  const monitoringEventRuleId = item.monitoring_event_rule_id;
  const monitoringEventRule
    = new ContactMonitoringEventRule(monitoringTargetId, monitoringEventRuleId);
  monitoringEventRule.setApiResponseJosn(item);

  return monitoringEventRule;
}

function makeRequestJson(monitoringEventRule) {
  return monitoringEventRule.getApiRequestJson();
}

const ContactMonitoringEventRulesRepository = {
  async store(monitoringEventRule) {
    const urlParams = {
      monitoringTargetId: monitoringEventRule.monitoringTargetId,
    };
    const requestParams = makeRequestJson(monitoringEventRule);
    const response = await Repository.post(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact/monitoring-device-id/unspecified`, requestParams);

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      if (response.data.errors[0].error_cd
          === ContactMonitoringEventRuleAlreadyExistsError.getErrorCd()
      ) {
        throw new ContactMonitoringEventRuleAlreadyExistsError();
      } else {
        throw new ContactMonitoringEventRulesRepositoryError();
      }
    }
    // 結果の返却
    return getInstance(response.data.item);
  },

  async update(monitoringEventRule) {
    const urlParams = {
      monitoringTargetId: monitoringEventRule.monitoringTargetId,
    };
    urlParams.monitoringEventRuleId = monitoringEventRule.monitoringEventRuleId;
    const requestParams = makeRequestJson(monitoringEventRule);
    const response = await Repository.put(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact/${urlParams.monitoringEventRuleId}`, requestParams);

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new ContactMonitoringEventRulesRepositoryError();
    }
    // 結果の返却
    return getInstance(response.data.item);
  },

  async index(monitoringTargetId) {
    const urlParams = {
      monitoringTargetId,
    };
    const response = await Repository.get(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new ContactMonitoringEventRulesRepositoryError();
    }

    const items = [];
    response.data.items.forEach((item) => {
      items.push(getInstance(item));
    });
    return items;
  },

  async get(monitoringTargetId, monitoringEventRuleId) {
    const urlParams = {
      monitoringTargetId,
    };
    urlParams.monitoringEventRuleId = monitoringEventRuleId;
    const response = await Repository.get(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact/${urlParams.monitoringEventRuleId}`, {
      params: {},
    });

    // 処理結果のチェック
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new ContactMonitoringEventRulesRepositoryError();
    }

    return getInstance(response.data.item);
  },

  async destroy(monitoringTargetId, monitoringEventRuleId) {
    const urlParams = {
      monitoringTargetId,
    };
    urlParams.monitoringEventRuleId = monitoringEventRuleId;
    const response = await Repository.delete(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact/${urlParams.monitoringEventRuleId}`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new ContactMonitoringEventRulesRepositoryError();
    }
  },

  async getMonitoringDeviceIdUnspecified(monitoringTargetId, {
    nullable = false,
  }) {
    const urlParams = {
      monitoringTargetId,
    };
    const response = await Repository.get(`${RESOURCE}/${urlParams.monitoringTargetId}/monitoring-event-rules/contact/monitoring-device-id/unspecified`, {
      params: {},
    });

    // 処理結果のチェック
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      // データがなければ空を返す
      if (response.data.errors[0].error_cd
            === ContactMonitoringEventRuleNotFoundError.getErrorCd()
      ) {
        if (nullable) {
          return null;
        }
        throw new ContactMonitoringEventRuleNotFoundError();
      } else {
        throw new ContactMonitoringEventRulesRepositoryError();
      }
    }

    return getInstance(response.data.item);
  },

  async upsert(monitoringEventRule) {
    let res = null;
    if (monitoringEventRule.monitoringEventRuleId === null) {
      res = this.store(monitoringEventRule);
    } else {
      res = this.update(monitoringEventRule);
    }
    return res;
  },
};

/* フォームアイテム */
class ContactMonitoringEventRuleFormItem {
  constructor() {
    this._contactMonitoringEventRule;
    this._oldEventStartNotification;
    this._oldEventEndNotification;
  }

  get contactMonitoringEventRule() {
    return this._contactMonitoringEventRule;
  }

  set contactMonitoringEventRule(value) {
    this._contactMonitoringEventRule = value;
  }

  get oldEventStartNotification() {
    return this._oldEventStartNotification;
  }

  set oldEventStartNotification(value) {
    this._oldEventStartNotification = value;
  }

  get oldEventEndNotification() {
    return this._oldEventEndNotification;
  }

  set oldEventEndNotification(value) {
    this._oldEventEndNotification = value;
  }

  /* 算出プロパティ */

  /* イベントハンドラ */

  /* 初期化・登録処理 */
  static async createInstance(monitoringTarget, { // eslint-disable-line
    defaultNotificationStatusCd = NOTIFICATION_STATUS_CD.ON,
  }) {
    // 初期化(DBの値をフォーム反映)
    // 接点監視ルールの取得
    // TODO: 監視対象全体の設定を取得するようにする・・・現状は監視対象別のデータも取得している

    // データが存在しない場合
    const contactMonitoringEventRules = await ContactMonitoringEventRulesRepository.index(
      monitoringTarget.monitoringTargetId,
    );
    let contactMonitoringEventRule = null;
    if (contactMonitoringEventRules.length > 0) {
      [contactMonitoringEventRule] = contactMonitoringEventRules;
    }

    // インスタンス生成
    const contactMonitoringEventRuleFormItem = new ContactMonitoringEventRuleFormItem();
    if (!contactMonitoringEventRule) {
      // 接点監視ルールが存在すればその値を反映する
      contactMonitoringEventRuleFormItem.contactMonitoringEventRule = contactMonitoringEventRule;
    } else {
      // 存在しなければ、新しくインスタンスを生成する
      const newContactMonitoringEventRule
        = new ContactMonitoringEventRule(monitoringTarget.monitoringTargetId);
      newContactMonitoringEventRule.organizationId = monitoringTarget.organizationId;
      newContactMonitoringEventRule.projectId = monitoringTarget.projectId;

      // defaultNotificationStatuが
      newContactMonitoringEventRule.eventStartNotification.statusCd = defaultNotificationStatusCd;
      newContactMonitoringEventRule.eventEndNotification.statusCd = defaultNotificationStatusCd;

      contactMonitoringEventRuleFormItem.contactMonitoringEventRule = newContactMonitoringEventRule;
    }
    /* eslint-enable */

    // 初期状態の通知設定を保存
    contactMonitoringEventRuleFormItem.oldEventStartNotification
      = contactMonitoringEventRule.eventStartNotification;
    contactMonitoringEventRuleFormItem.oldEventEndNotification
      = contactMonitoringEventRule.eventEndNotification;

    return contactMonitoringEventRuleFormItem;
  }

  createContactMonitoringEventRule() {
    // インスタンス生成（フォームの値からインスタンス生成）
    const contactMonitoringEventRule = this.contactMonitoringEventRule.copy();

    // 通知がOFFの場合は、件名更新を行わない
    if (
      this.contactMonitoringEventRule.eventStartNotification.statusCd
        === NOTIFICATION_STATUS_CD.OFF
    ) {
      contactMonitoringEventRule.eventStartNotification.title
        = this.oldEventStartNotification.title;
    }
    if (
      this.contactMonitoringEventRule.eventEndNotification.statusCd
        === NOTIFICATION_STATUS_CD.OFF
    ) {
      contactMonitoringEventRule.eventEndNotification.title
        = this.oldEventEndNotification.title;
    }
    return contactMonitoringEventRule;
  }

  // 登録/更新
  async upsert() {
    const updateItem = await ContactMonitoringEventRulesRepository.upsert(
      this.createContactMonitoringEventRule(),
    );
    return updateItem;
  }
}

export {
  ContactMonitoringEventRulesRepository,
  ContactMonitoringEventRulesRepositoryError,
  ContactMonitoringEventRuleAlreadyExistsError,
  ContactMonitoringEventRuleFormItem,
};
